import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Empty, Input, Menu, Popover, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import { MemberUserDto } from '@api/Api';
import { useGetPotentialsApprovalManagers } from '@hooks/queries/approval';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useDebounced } from '@helpers/useDebounced';
import { ReactComponent as DownArrowSvg } from '@assets/icons/arrow-down.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';

import PotentialManager from './components/PotentialManager/PotentialManager';
import PotentialManagerSkeleton from './components/PotentialManager/PotentialManagerSkeleton';

import './PotentialsManagersFilter.less';

let animationFrameId: number;

type PotentialsManagersFilterProps = {
  onChange: (value: string[]) => void;
  selectedManagerEmails: string[];
};

export default memo(function PotentialsManagersFilter({
  onChange,
  selectedManagerEmails
}: PotentialsManagersFilterProps) {
  const [workspace] = useCurrentWorkspace();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchQuery = useDebounced(search, 600);

  const { isLoading, data } = useGetPotentialsApprovalManagers({
    enabled: open,
    params: {
      searchQuery,
      workspaceId: workspace?.id || ''
    }
  });
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const list = useMemo(() => {
    return data?.pages.flatMap((x) => x.edges.map((x) => x.node)) ?? [];
  }, [data]);

  const sortBySelected = useCallback(
    (list: MemberUserDto[]) =>
      list
        .map((x, i) => ({ item: x, index: i }))
        .sort((a, b) => {
          const selectedIndexA = selectedManagerEmails.findIndex(
            (x) => x === a.item.email
          );
          const selectedIndexB = selectedManagerEmails.findIndex(
            (x) => x === b.item.email
          );
          if (selectedIndexA !== -1 && selectedIndexB !== -1)
            return selectedIndexA - selectedIndexB;
          if (selectedIndexA !== -1 && selectedIndexB === -1) return -1;
          if (selectedIndexA === -1 && selectedIndexB !== -1) return 1;
          return a.index - b.index;
        })
        .map((x) => x.item),
    []
  );

  const popupRef = useRef<HTMLDivElement>(null);
  const onOpenChange = useCallback(
    (open: boolean) => {
      setOpen(open);
      cancelAnimationFrame(animationFrameId);
      if (open) {
        animationFrameId = requestAnimationFrame(() => {
          const menu = popupRef.current?.querySelector('.ant-menu');
          menu?.scrollTo(0, 0);
        });
      }
    },
    [sortBySelected]
  );

  const onSelect = ({ key }: { key: string }) => {
    onChangeRef.current([...selectedManagerEmails, key]);
    setOpen(false);
  };

  const onDeselect = ({ key }: { key: string }) => {
    onChange(selectedManagerEmails.filter((x) => x !== key));
  };

  const menuItems = useMemo(() => {
    const result: MenuItemType[] = list.map((x) => ({
      key: x.email,
      label: <PotentialManager userData={x} />
    }));
    if (isLoading && !list.length) {
      result.push({
        key: 'skeleton',
        label: <PotentialManagerSkeleton />
      });
    } else if (!list.length) {
      result.push({
        key: 'empty',
        disabled: true,
        label: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      });
    }
    return result;
  }, [list, isLoading]);

  return (
    <Popover
      trigger="click"
      open={open}
      placement="bottomLeft"
      onOpenChange={onOpenChange}
      overlayClassName="potentials-managers-filter_popover"
      content={
        <div ref={popupRef}>
          <div className="potentials-managers-filter_search">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name or email"
              size="large"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </div>
          <Menu
            className="assignees-menu"
            onSelect={onSelect}
            onDeselect={onDeselect}
            selectedKeys={selectedManagerEmails}
            multiple
            items={menuItems}
          />
        </div>
      }
    >
      <div style={{ width: '100%', maxWidth: 130 }} className="filter-item">
        {!!selectedManagerEmails.length && (
          <Button
            type="text"
            className="filter-item__clear"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          >
            <CrossSvg />
          </Button>
        )}
        <span
          className={classNames('filter-item__name', {
            'filter-item__active-name': !!selectedManagerEmails.length
          })}
        >
          {selectedManagerEmails.length
            ? selectedManagerEmails.join(', ')
            : 'Manager'}
        </span>
        <div className="filter-item-icon">
          <DownArrowSvg />
        </div>
      </div>
    </Popover>
  );
});
